import { useId, useState, type FormEvent } from 'react';
import { fieldStyle, StatusDot, token } from '@bp-files/ui';
import { messageFrom } from '@bp-files/client';
import { setMyName } from '@/lib/api';
import { SIGN_IN_LANDING_COPY } from './signInLanding.copy';

/**
 * The third step, and the one that is not about a credential (USR-1).
 *
 * **It is here because Entra will not collect a display name for us.**
 * ADR-0038 took a member's name from their token's `name` claim at activation,
 * which works for a directory that knows who they are; an Entra External ID
 * sign-up gathers none, so a local account arrives claiming `"unknown"` and the
 * API drops it rather than writing a word that names nobody. Somebody who has
 * just been provisioned therefore has the address USR-2 recorded and nothing
 * else — and the greeting on this very page would read as their own email.
 *
 * **Driven by `/me`'s `needsName`, not by the passcode flow.** A person can
 * reach it having signed in with a passkey on a second device, or having
 * abandoned this step once and come back; the question is a fact about the
 * roster row, so the row is what answers it. That also keeps it out of
 * `PasscodeForm`, which owns an anonymous conversation and should not learn
 * about sessions.
 *
 * **Once.** The API answers 409 to a second attempt, and there is no rename
 * surface anywhere — USR-1 describes none, and a member renaming themselves on
 * the screen an admin uses to decide access is a different feature.
 */

const FIELD_CLASS = 'w-full border px-3 py-2 text-base';

const BUTTON_CLASS =
  'inline-flex min-h-11 min-w-56 items-center justify-center rounded-md border px-8 text-base font-semibold transition-colors outline-none focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-70';

function buttonStyle() {
  return {
    borderColor: token('bp-blue'),
    background: token('bp-blue-deep'),
    color: token('bp-on-accent'),
  };
}

export interface NameFormProps {
  /** The address the roster holds, so an empty answer can be refused locally. */
  readonly email: string;
  /** Re-run the session check, so `needsName` is re-read rather than assumed. */
  readonly onNamed: () => Promise<void>;
}

export function NameForm({ email, onNamed }: NameFormProps) {
  const [name, setName] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const fieldId = useId();
  const hintId = `${fieldId}-hint`;
  const copy = SIGN_IN_LANDING_COPY.passcode.name;

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (pending) return;

    setError(undefined);
    const given = name.trim();

    // The API refuses both of these too. Checking here saves a round trip on
    // the two mistakes that are certain to happen, and the API is still what
    // decides — SEC-5's rule, applied to a field rather than to access.
    if (given === '' || given.toLowerCase() === email.trim().toLowerCase()) {
      setError(copy.invalid);
      return;
    }

    void save(given);
  }

  async function save(given: string): Promise<void> {
    setPending(true);
    try {
      await setMyName(given);
      await onNamed();
    } catch (caught) {
      setError(messageFrom(caught, SIGN_IN_LANDING_COPY.passcode.fallback));
      setPending(false);
    }
  }

  return (
    <form onSubmit={submit} className="max-w-md">
      <label htmlFor={fieldId} className="mb-2 block text-sm font-medium text-body">
        {copy.label}
      </label>

      <input
        id={fieldId}
        name="name"
        type="text"
        autoComplete="name"
        value={name}
        autoFocus
        onChange={(event) => {
          setName(event.target.value);
        }}
        aria-describedby={hintId}
        className={FIELD_CLASS}
        style={fieldStyle()}
      />

      <p id={hintId} className="mt-2 mb-5 text-sm text-muted">
        {copy.hint}
      </p>

      {error === undefined ? null : (
        <p role="alert" className="mb-5 flex items-center gap-2 text-sm text-danger">
          <StatusDot tone="alert" />
          {error}
        </p>
      )}

      <button type="submit" disabled={pending} className={BUTTON_CLASS} style={buttonStyle()}>
        {pending ? copy.pending : copy.submit}
      </button>
    </form>
  );
}
